import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RcsMessage, MessageType, RcsMessageConfig, Recipient } from '../../../../_models/messaging/messages/rcs-messages/rcs-message.model';

@Injectable({
  providedIn: 'root'
})
export class RcsService {
  private currentMessage$ = new BehaviorSubject<RcsMessage | null>(null);
  private defaultFrom = 'a_176337784764418400';
  private defaultRecipient: Recipient = {
    msisdn: ['+447525264942'],
    correlationId: 'na1b2b1i1',
    substitutions: {
      text: 'Hello Matthew Cisco Test'
    },
    options: {
      smsFallback: true,
      smsSenderId: '447378905205',
      text: 'fallback text',
      domain: 'https://s.imiconnect.co'
    }
  };
  
  constructor() {
    // Initialize with a default SMS message
    this.currentMessage$.next({
      type: MessageType.SMS,
      smsText: '',
      timestamp: new Date()
    });
  }
  
  getCurrentMessage(): Observable<RcsMessage | null> {
    return this.currentMessage$.asObservable();
  }
  
  updateMessage(message: RcsMessage): void {
    this.currentMessage$.next({
      ...message,
      timestamp: new Date()
    });
  }
  
  resetMessage(): void {
    this.currentMessage$.next({
      type: MessageType.SMS,
      smsText: '',
      timestamp: new Date()
    });
  }

  generateConfig(message: RcsMessage, from?: string, recipient?: Recipient, callbackData?: string): RcsMessageConfig {
    const config: RcsMessageConfig = {
      channel: message.type === MessageType.SMS ? 'sms' : 'rcs',
      from: from || this.defaultFrom,
      to: recipient ? [recipient] : [this.defaultRecipient],
      callbackData: callbackData || 'clientId:1',
      content: {
        type: message.type === MessageType.SMS ? MessageType.TEXT : message.type
      }
    };

    if (message.type === MessageType.SMS) {
      config.content.text = message.smsText || '';
    } else if (message.type === MessageType.TEXT) {
      config.content.text = message.text || '';
      if (message.suggestions && message.suggestions.length > 0) {
        // Filter out undefined/null values from suggestions
        config.content.suggestions = message.suggestions.map(s => {
          const cleanSuggestion: any = {
            type: s.type,
            displayText: s.displayText
          };
          if (s.url) cleanSuggestion.url = s.url;
          if (s.phone) cleanSuggestion.phone = s.phone;
          if (s.address) cleanSuggestion.address = s.address;
          if (s.latitude !== undefined) cleanSuggestion.latitude = s.latitude;
          if (s.longitude !== undefined) cleanSuggestion.longitude = s.longitude;
          if (s.startTime) cleanSuggestion.startTime = s.startTime;
          if (s.endTime) cleanSuggestion.endTime = s.endTime;
          if (s.meetingTitle) cleanSuggestion.meetingTitle = s.meetingTitle;
          if (s.meetingDescription) cleanSuggestion.meetingDescription = s.meetingDescription;
          return cleanSuggestion;
        });
      }
    } else if (message.type === MessageType.STANDALONE && message.richCard) {
      // Deep copy and clean the richCard
      const richCard: any = {
        title: message.richCard.title,
        description: message.richCard.description
      };
      if (message.richCard.orientation) richCard.orientation = message.richCard.orientation;
      if (message.richCard.thumbnailAlignment) richCard.thumbnailAlignment = message.richCard.thumbnailAlignment;
      if (message.richCard.media && message.richCard.media.url) {
        richCard.media = {
          url: message.richCard.media.url,
          height: message.richCard.media.height || 'MEDIUM'
        };
      }
      if (message.richCard.thumbnail && message.richCard.thumbnail.url) {
        richCard.thumbnail = {
          url: message.richCard.thumbnail.url,
          height: message.richCard.thumbnail.height || 'MEDIUM'
        };
      }
      if (message.richCard.suggestions && message.richCard.suggestions.length > 0) {
        richCard.suggestions = message.richCard.suggestions.map(s => {
          const cleanSuggestion: any = {
            type: s.type,
            displayText: s.displayText
          };
          if (s.url) cleanSuggestion.url = s.url;
          if (s.phone) cleanSuggestion.phone = s.phone;
          if (s.address) cleanSuggestion.address = s.address;
          if (s.latitude !== undefined) cleanSuggestion.latitude = s.latitude;
          if (s.longitude !== undefined) cleanSuggestion.longitude = s.longitude;
          if (s.startTime) cleanSuggestion.startTime = s.startTime;
          if (s.endTime) cleanSuggestion.endTime = s.endTime;
          if (s.meetingTitle) cleanSuggestion.meetingTitle = s.meetingTitle;
          if (s.meetingDescription) cleanSuggestion.meetingDescription = s.meetingDescription;
          return cleanSuggestion;
        });
      }
      config.content.richCard = richCard;
    } else if (message.type === MessageType.CAROUSEL && message.carousel) {
      config.content.width = message.carousel.width;
      if (message.carousel.cards && message.carousel.cards.length > 0) {
        config.content.cards = message.carousel.cards.map(card => {
          const cleanCard: any = {
            title: card.title,
            description: card.description
          };
          if (card.media && card.media.url) {
            cleanCard.media = {
              url: card.media.url,
              height: card.media.height || 'MEDIUM'
            };
          }
          if (card.thumbnail && card.thumbnail.url) {
            cleanCard.thumbnail = {
              url: card.thumbnail.url,
              height: card.thumbnail.height || 'MEDIUM'
            };
          }
          if (card.suggestions && card.suggestions.length > 0) {
            cleanCard.suggestions = card.suggestions.map(s => {
              const cleanSuggestion: any = {
                type: s.type,
                displayText: s.displayText
              };
              if (s.url) cleanSuggestion.url = s.url;
              if (s.phone) cleanSuggestion.phone = s.phone;
              return cleanSuggestion;
            });
          }
          return cleanCard;
        });
      }
      if (message.suggestions && message.suggestions.length > 0) {
        config.content.suggestions = message.suggestions.map(s => {
          const cleanSuggestion: any = {
            type: s.type,
            displayText: s.displayText
          };
          if (s.url) cleanSuggestion.url = s.url;
          if (s.phone) cleanSuggestion.phone = s.phone;
          if (s.address) cleanSuggestion.address = s.address;
          if (s.latitude !== undefined) cleanSuggestion.latitude = s.latitude;
          if (s.longitude !== undefined) cleanSuggestion.longitude = s.longitude;
          if (s.startTime) cleanSuggestion.startTime = s.startTime;
          if (s.endTime) cleanSuggestion.endTime = s.endTime;
          if (s.meetingTitle) cleanSuggestion.meetingTitle = s.meetingTitle;
          if (s.meetingDescription) cleanSuggestion.meetingDescription = s.meetingDescription;
          return cleanSuggestion;
        });
      }
    }

    return config;
  }
}
